import { ComponentIds, Entity, HierarchyUtils } from '@inter-ikea-core/core';
import {
  CoreApi,
  Dimension,
  Product,
  ProductType,
  ReplacedFrameComponent
} from '@inter-ikea-storageone/range';
import { CornerSolutionComponent } from '@inter-ikea-storageone/range';
import { getMatchingFrames } from './productUtils';
import { getProductFromEntity } from 'rangeConfig/shared/utils/getProductFromEntity';
import { getMatchingDoorsWithHandles, getDoorsFromSelectedEntity } from './doorUtils';
import replaceDoors from './replaceDoors';
import {
  replaceFrameWithSlidingDoors,
  changeFrameSize,
  updateOuterLightsOnFrame,
  updateInnerLightsOnFrame,
  findMatchingCornerSolution,
  addShelfToTopOfFrame
} from './frameUtils';

const changeSizeCornerFrames = (
  coreApi: CoreApi,
  cornerFrames: Entity[],
  dimensionType: Dimension,
  size: number,
  products: Product[]
) => {
  cornerFrames.forEach(cornerFrame => {
    const previousDoors = getDoorsFromSelectedEntity(coreApi, cornerFrame, products);
    const doorsMatchingCurrentDoorsWithHandles = getMatchingDoorsWithHandles(
      previousDoors,
      products,
      coreApi,
      dimensionType
    ) as { doors: Product[]; handleId?: string }[];

    const newCornerProduct = findMatchingCornerSolution(
      cornerFrame,
      dimensionType,
      size,
      products,
      coreApi
    );
    if (!newCornerProduct) return;

    const allowedMovementOfChildrenInMilliMeter = dimensionType === 'height' ? 1 : Number.MAX_VALUE;
    const replaceInvalidEntities = dimensionType === 'height' ? false : true;

    const newFrameEntity = coreApi.products.replaceEntityWithHierarchy(
      cornerFrame.id,
      newCornerProduct.coreProductId,
      replaceInvalidEntities,
      allowedMovementOfChildrenInMilliMeter
    );
    if (newFrameEntity) {
      const regularFrame = HierarchyUtils.children(newFrameEntity).find(child =>
        child.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
      );
      if (regularFrame && dimensionType === 'height' && size === 2360) {
        addShelfToTopOfFrame(coreApi, regularFrame, products);
      }
      if (doorsMatchingCurrentDoorsWithHandles) {
        addReplacedFrameComponent(newFrameEntity);
        replaceDoors(coreApi, newFrameEntity, doorsMatchingCurrentDoorsWithHandles);
      }
    }
  });
};

const changeSizeNormalFrames = (
  coreApi: CoreApi,
  frames: Entity[],
  dimensionType: Dimension,
  size: number,
  products: Product[]
) => {
  frames.forEach(frame => {
    const previousDoors = getDoorsFromSelectedEntity(coreApi, frame, products);
    const doorsMatchingCurrentDoorsWithHandles = getMatchingDoorsWithHandles(
      previousDoors,
      products,
      coreApi,
      dimensionType
    ) as { doors: Product[]; handleId?: string }[];

    let newFrame: any;
    const product = getProductFromEntity(frame, products, coreApi);
    const slidingDoorOnFrame = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(frame, [
      ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
      ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
    ]);
    const outerLightsOnFrame = coreApi.entities.getChildrenWithProductType(
      frame,
      ProductType.PAX_OUTER_LIGHT
    );
    const innerLightsOnFrame = [
      ...coreApi.entities.getChildrenWithProductType(frame, ProductType.PAX_INNER_LIGHT),
      ...coreApi.entities.getChildrenWithProductType(frame, ProductType.PAX_INNER_LIGHT_OVERSIDAN)
    ];

    if (!product) return;
    const matchFrameBy = dimensionType === 'height' ? 'depth' : 'height';
    const matchingFrames = getMatchingFrames(product, products, matchFrameBy);
    if (slidingDoorOnFrame) {
      newFrame = replaceFrameWithSlidingDoors(
        slidingDoorOnFrame,
        products,
        size,
        frame,
        coreApi,
        matchingFrames,
        doorsMatchingCurrentDoorsWithHandles,
        matchFrameBy,
        dimensionType
      );
    } else {
      newFrame = changeFrameSize(coreApi, matchingFrames, size, frame, dimensionType, products);
    }

    if (newFrame) {
      addReplacedFrameComponent(newFrame);
      updateOuterLightsOnFrame(newFrame, outerLightsOnFrame, coreApi);
      updateInnerLightsOnFrame(newFrame, innerLightsOnFrame, coreApi);
      if (doorsMatchingCurrentDoorsWithHandles && dimensionType === 'height') {
        replaceDoors(coreApi, newFrame, doorsMatchingCurrentDoorsWithHandles);
      }
    }
  });
};

export const addReplacedFrameComponent = (frame: Entity) => {
  if (frame) {
    frame.addComponent(new ReplacedFrameComponent());
  }
};

export const changeSolutionSize = (
  coreApi: CoreApi,
  dimensionType: Dimension,
  size: number,
  products: Product[]
) => {
  const allFramesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const cornerFramesInScene = coreApi.entities.getByComponent(CornerSolutionComponent.id);
  const framesInCornerSolutions = cornerFramesInScene.flatMap(cornerFrame =>
    HierarchyUtils.descendants(cornerFrame).filter(frame =>
      frame.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
    )
  );
  const normalFramesInScene = allFramesInScene.filter(
    frame => !framesInCornerSolutions.includes(frame)
  );

  changeSizeCornerFrames(coreApi, cornerFramesInScene, dimensionType, size, products);
  changeSizeNormalFrames(coreApi, normalFramesInScene, dimensionType, size, products);
};
